import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MetroBuilding from './MetroBuilding';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { GroupAnimation } from './types';
import { calculateGroups } from './utils';
import Group from './Group';
import { useIsLandscape } from '../../../../hooks/useIsLandscape';

type Props = {
  className?: string;
  groupSizeRange: [number, number];
  peopleInMetro: number;
  onChangePeopleInMetro: (value: number) => void;
  groupMovements: number;
  groupAppearanceFrequency: number;
  groupExitTime: number;
  isGameOnPause: boolean;
  isProgressOnPause: boolean;
};

const CalculationContainer: FC<Props> = ({
  className,
  groupSizeRange,
  peopleInMetro,
  onChangePeopleInMetro,
  groupAppearanceFrequency,
  groupMovements,
  groupExitTime,
  isGameOnPause,
  isProgressOnPause,
}) => {
  const [animations, setAnimations] = useState<GroupAnimation[]>([]);
  const [isStarted, setIsStarted] = useState<boolean | null>(null);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingMovementsRef = useRef(groupMovements);
  const currentPeopleCountInMetroRef = useRef(peopleInMetro);
  const frequencyAnimationTime = groupAppearanceFrequency * 1000;
  const isLandscape = useIsLandscape();

  const pauseRef = useRef({
    remainingTime: 0,
    startTime: 0,
  });

  const startAnimation = useCallback(() => {
    const { animation } = calculateGroups(currentPeopleCountInMetroRef.current, groupSizeRange, remainingMovementsRef.current);

    if (animation.direction === 'out') {
      currentPeopleCountInMetroRef.current += -animation.size;
    }

    setAnimations((prevAnimations) => [...prevAnimations, animation]);
    remainingMovementsRef.current--;

    if (remainingMovementsRef.current > 0) {
      pauseRef.current.startTime = Date.now();
      pauseRef.current.remainingTime = 0;
      animationTimeoutRef.current = setTimeout(startAnimation, frequencyAnimationTime);
    } else {
      setIsStarted(false);
    }
  }, [frequencyAnimationTime, groupSizeRange]);

  const pauseAnimation = useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;

      pauseRef.current.remainingTime =
        (pauseRef.current.remainingTime ? pauseRef.current.remainingTime : frequencyAnimationTime) -
        (Date.now() - pauseRef.current.startTime);
    }
  }, [frequencyAnimationTime]);

  const resumeAnimation = useCallback(() => {
    if (pauseRef.current.remainingTime > 0 && remainingMovementsRef.current > 0) {
      pauseRef.current.startTime = Date.now();

      animationTimeoutRef.current = setTimeout(() => {
        startAnimation();
      }, pauseRef.current.remainingTime);
    }
  }, [startAnimation]);

  useEffect(() => {
    if (isGameOnPause) {
      pauseAnimation();
    } else {
      resumeAnimation();
    }
  }, [isGameOnPause, pauseAnimation, resumeAnimation]);

  useEffect(() => {
    if (isProgressOnPause && remainingMovementsRef.current > 0) {
      setIsStarted(true);
      startAnimation();
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [groupAppearanceFrequency, groupSizeRange, isProgressOnPause, peopleInMetro, startAnimation]);

  useEffect(() => {
    if (isStarted === false && !animations.length && !isLandscape) {
      onChangePeopleInMetro(currentPeopleCountInMetroRef.current);
      setIsStarted(null);
      animationTimeoutRef.current = null;
    }
  }, [animations.length, isStarted, onChangePeopleInMetro, isLandscape]);

  const removeAnimation = (id: number) => {
    setAnimations((prev) => prev.filter((animation) => animation.id !== id));
  };

  const isLeftOpenDoor = useMemo(() => {
    return isProgressOnPause && Boolean(animations.filter((animation) => animation.direction === 'in').length);
  }, [isProgressOnPause, animations]);

  const isRightOpenDoor = useMemo(() => {
    return isProgressOnPause && Boolean(animations.filter((animation) => animation.direction === 'out').length);
  }, [isProgressOnPause, animations]);

  const handleAnimationEnd = (params: GroupAnimation) => {
    removeAnimation(params.id);

    if (params.direction === 'in') {
      currentPeopleCountInMetroRef.current += params.size;
    }
  };

  const inAnimations = useMemo(() => animations.filter((a) => a.direction === 'in'), [animations]);

  const outAnimations = useMemo(() => animations.filter((a) => a.direction === 'out'), [animations]);

  return (
    <div className={classNames(styles.calculationContainer, className)}>
      <div className={styles.metroBuildingContainer}>
        <MetroBuilding isLeftOpenDoor={isLeftOpenDoor} isRightOpenDoor={isRightOpenDoor} />
      </div>

      <div className={styles.groups}>
        <div className={styles.inMetro}>
          {inAnimations.map((animation) => (
            <Group
              isPaused={isGameOnPause}
              key={animation.id}
              animation={animation}
              duration={groupExitTime}
              onAnimationEnd={handleAnimationEnd}
            />
          ))}
        </div>
        <div className={styles.outMetro}>
          {outAnimations.map((animation) => (
            <Group
              isPaused={isGameOnPause}
              key={animation.id}
              animation={animation}
              duration={groupExitTime}
              onAnimationEnd={handleAnimationEnd}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalculationContainer;
